import { z } from 'zod';
/**
 * Key AI **của một dự án** — docs/plan-project-ai-key.md.
 *
 * ## Dự án TRỎ TỚI một key đã có, không giữ bản sao
 *
 * Key là dữ liệu **ghi một chiều** (docs/security.md §6): nhập vào rồi gửi lên,
 * không bao giờ đọc ngược về. Nên "gợi ý lấy key của tôi" không thể là điền sẵn
 * chuỗi key vào một form — trình duyệt không đọc nổi nó. Thay vào đó dự án giữ
 * `aiKeyId` trỏ tới một hàng `ai_provider_key` sẵn có, và bí mật không bao giờ
 * rời khỏi cơ sở dữ liệu.
 *
 * ## Chỉ gắn được key của CHÍNH MÌNH
 *
 * Gắn key là cho cả đội tiêu tiền của chủ key. Cho phép gắn key của người khác
 * nghĩa là một PM tiêu tiền của đồng nghiệp mà người đó không bấm nút nào —
 * cửa này nằm ở command handler, không ở giao diện.
 */
export const ProjectAiKeyOption = z.object({
    id: z.uuid(),
    label: z.string(),
    /** `sk-ant-…7f3a` — bốn ký tự cuối là tất cả những gì được lộ ra. */
    keyLast4: z.string().length(4),
    provider: z.string(),
    /** Key đang hoạt động của chính người đang xem — giao diện chọn sẵn cái này. */
    isActive: z.boolean(),
    /** `null` ⇒ chưa kiểm kết nối, và khi ấy không gắn vào dự án được. */
    verifiedAt: z.iso.datetime().nullable(),
});
export const ProjectAiKeyView = z.object({
    projectId: z.uuid(),
    /** Key đang gắn; `null` = dự án chưa gắn, mỗi người dùng key của mình. */
    attached: z
        .object({
        keyId: z.uuid(),
        label: z.string(),
        keyLast4: z.string().length(4),
        /** Tên chủ key — cả đội đang tiêu tiền của người này, nên phải nói ra. */
        ownerName: z.string(),
        /** Chủ key có phải chính người đang xem không — quyết định có gỡ được hay không. */
        ownedByMe: z.boolean(),
        /** Chủ đã hạ key xuống ⇒ dự án rơi về key cá nhân của từng người. */
        isActive: z.boolean(),
    })
        .nullable(),
    /**
     * Key của **chính người đang xem** để chọn — đây là phần "gợi ý tự động".
     *
     * Rỗng nghĩa là họ chưa có key nào; khi ấy giao diện chỉ đường sang trang cài
     * đặt cá nhân thay vì hiện một ô chọn trống.
     */
    myKeys: z.array(ProjectAiKeyOption),
    /** Tiền AI dự án đã tiêu, **USD** — xem `ProjectAiSpend`. */
    spentUsd: z.number().nonnegative(),
});
export const AttachProjectAiKeyInput = z.object({
    /** Phải là key của chính người gọi, và phải đã kiểm kết nối. */
    keyId: z.uuid(),
});
/**
 * Tiền AI một dự án đã tiêu — docs/plan-project-ai-key.md.
 *
 * ## ⚠️ USD, và **không** cộng vào tổng ngân sách
 *
 * `ai_usage_log.cost_usd` là USD; `project_budget.currency` mặc định VND; và hệ
 * thống không có bảng tỷ giá nào. Cộng một số USD vào một tổng VND là sai âm
 * thầm — con số vẫn hiện ra bình thường, chỉ là sai. Nên nó đứng thành một dòng
 * riêng, nói rõ đơn vị, cho tới khi có ai đó quyết định nguồn tỷ giá.
 *
 * Đây là số **dẫn xuất**: tính từ `ai_usage_log` lúc đọc, không lưu thành
 * `project_cost_item`. Vật chất hoá nó là tạo một bản sao sẽ lệch khỏi bản gốc
 * đúng vào lúc ai đó tính lại.
 */
export const ProjectAiSpend = z.object({
    projectId: z.uuid(),
    spentUsd: z.number().nonnegative(),
    /** Số lời gọi đã tính tiền — để con số trên có chỗ bấu víu. */
    callCount: z.number().int().nonnegative(),
});
//# sourceMappingURL=project-ai-key.js.map